<script setup lang="ts">
import Multiselect from 'vue-multiselect'
import { computed } from 'vue'

type DropdownOption = string | number | Record<string, unknown>
type DropdownValue = DropdownOption | null

const props = withDefaults(
  defineProps<{
    modelValue: DropdownValue
    options: DropdownOption[]
    label?: string
    trackBy?: string
    disabled?: boolean
    loading?: boolean
    searchable?: boolean
    closeOnSelect?: boolean
    showLabels?: boolean
    optionsLimit?: number
    openDirection?: string
    internalSearch?: boolean
    placeholder?: string
    ariaLabel?: string
    clearable?: boolean
    clearValue?: DropdownValue
    onClear?: (previousValue: DropdownValue) => void
    noResultText?: string
    noOptionsText?: string
  }>(),
  {
    label: undefined,
    trackBy: undefined,
    disabled: false,
    loading: false,
    searchable: true,
    closeOnSelect: true,
    showLabels: false,
    optionsLimit: 1000,
    openDirection: 'bottom',
    internalSearch: false,
    placeholder: 'Vyberte hodnotu',
    ariaLabel: 'Výber hodnoty',
    clearable: true,
    clearValue: '',
    noResultText: 'Nenašli sa žiadne výsledky.',
    noOptionsText: 'Zoznam je prázdny.',
  },
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: DropdownValue): void
  (e: 'search-change', query: string): void
  (e: 'clear', previousValue: DropdownValue): void
}>()

const hasSelection = computed(() => props.modelValue !== null && props.modelValue !== '')

function handleModelValueUpdate(value: unknown): void {
  if (Array.isArray(value)) {
    emit('update:modelValue', props.clearValue)
    return
  }

  if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'object') {
    emit('update:modelValue', value as DropdownValue)
    return
  }

  emit('update:modelValue', props.clearValue)
}

function handleSearchChange(query: string): void {
  emit('search-change', query)
}

function clearSelection(): void {
  const previousValue = props.modelValue
  props.onClear?.(previousValue)
  emit('clear', previousValue)
  emit('update:modelValue', props.clearValue)
}
</script>

<template>
  <div :class="{ 'has-selection': hasSelection }">
    <multiselect
      :model-value="props.modelValue"
      :options="props.options"
      :label="props.label"
      :track-by="props.trackBy"
      :disabled="props.disabled"
      :searchable="props.searchable"
      :close-on-select="props.closeOnSelect"
      :show-labels="props.showLabels"
      :options-limit="props.optionsLimit"
      :open-direction="props.openDirection"
      :internal-search="props.internalSearch"
      :loading="props.loading"
      :placeholder="props.placeholder"
      :aria-label="props.ariaLabel"
      @update:model-value="handleModelValueUpdate"
      @search-change="handleSearchChange"
    >
      <template #clear>
        <button
          v-if="props.clearable && hasSelection && !props.loading"
          type="button"
          class="multiselect__clear"
          :aria-label="`Vymazať: ${props.ariaLabel}`"
          @mousedown.prevent.stop="clearSelection"
        >
          ×
        </button>
      </template>

      <template #singleLabel="slotProps">
        <slot name="singleLabel" v-bind="slotProps" />
      </template>

      <template #noResult>{{ props.noResultText }}</template>
      <template #noOptions>{{ props.noOptionsText }}</template>
    </multiselect>
  </div>
</template>



<style scoped>
</style>
